import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import DigtectiveLogo from '../../assets/images/logos/digtective-logo-colorful.svg';
import { useOAuthConsent } from '../../apiHooks/queries/useOAuthConsent';
import { useOAuthConsentDecide } from '../../apiHooks/mutations/oauthMutations';
import { useAuthStore } from '../../store/authStore';

const OAuthConsent = () => {
  const { isLoggedIn } = useAuthStore();
  const { search } = useLocation();
  const state = new URLSearchParams(search).get('state');

  useEffect(() => {
    if (!isLoggedIn) {
      localStorage.setItem('oauth_return_url', window.location.pathname + window.location.search);
    }
  }, [isLoggedIn]);

  const { data, isLoading, isError } = useOAuthConsent(isLoggedIn ? state : null);
  const { mutateAsync: decide, isLoading: deciding } = useOAuthConsentDecide();
  const [error, setError] = useState<string | null>(null);

  if (!isLoggedIn) {
    return <Navigate to="/auth" replace />;
  }

  if (!state) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-5">
        <p className="text-red">Missing state parameter.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-5">
        <p className="text-gray-2">Loading authorization request…</p>
      </div>
    );
  }

  if (isError || !data?.data) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-5">
        <p className="text-red">Could not load this authorization request. It may have expired.</p>
      </div>
    );
  }

  const view = data.data;
  const submit = (decision: 'approve' | 'deny') => {
    setError(null);
    decide({ state, decision })
      .then(({ data: resp }) => {
        if (decision === 'approve' && resp?.continue_url) {
          window.location.assign(resp.continue_url);
        } else if (decision === 'deny') {
          window.location.assign('/dashboard');
        }
      })
      .catch(() => {
        setError('Could not record your decision. Please try again.');
      });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-5 px-4 py-10">
      <div className="w-full max-w-[480px] rounded-default border border-dark bg-white p-8 shadow">
        <div className="mb-6 flex justify-center">
          <img src={DigtectiveLogo} alt="Digtective" className="h-10 w-auto object-contain" />
        </div>

        <h1 className="mb-2 text-center text-xl font-semibold text-primary">
          Authorize {view.client.name}
        </h1>
        <p className="mb-1 text-center text-sm text-gray-2">
          <span className="font-semibold text-primary">{view.client.name}</span>
          {' is requesting access to '}
          <span className="font-semibold text-primary">{view.organization.name || `Organization #${view.organization.id}`}</span>
          .
        </p>
        <p className="mb-6 text-center text-xs text-gray-3">
          Redirect host:&nbsp;
          <code className="text-primary">{view.client.redirect_uri_host}</code>
          &nbsp;·&nbsp;
          {view.client.registration_type === 'dcr' ? 'Self-registered app' : 'Manually registered'}
        </p>

        <h2 className="mb-2 text-sm font-semibold text-primary">It will be able to:</h2>
        <ul className="mb-6 list-disc space-y-2 pl-5 text-sm text-gray-2 marker:text-primary">
          {view.scopes.map((s) => (
            <li key={s.id}>
              <span className="font-medium text-primary">{s.id}</span>
              <span className="text-gray-3"> — {s.description}</span>
            </li>
          ))}
        </ul>

        {view.existing_grant && (
          <p className="mb-4 text-xs text-gray-3">
            You have approved this app before; approving again will update the granted scopes.
          </p>
        )}

        {error && <p className="mb-3 text-sm text-red">{error}</p>}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="rounded-default border border-gray-4 px-4 py-2 text-sm text-primary hover:bg-gray-5 disabled:opacity-50"
            onClick={() => submit('deny')}
            disabled={deciding}
          >
            Deny
          </button>
          <button
            type="button"
            className="rounded-default bg-primary px-4 py-2 text-sm text-white hover:opacity-90 disabled:opacity-50"
            onClick={() => submit('approve')}
            disabled={deciding}
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  );
};

export default OAuthConsent;
